import { hooksApi } from '@verifime/api-definition';
import type { TEntityChoice } from '@verifime/components';
import { useEffect, useMemo, useRef } from 'react';
import {
  deriveConfirmEntityPlanState,
  type TConfirmEntityPlanLoaded,
  type TConfirmEntityPlanState,
} from '../confirmEntityPlanState';
import type { TEntityRequestType, TPlanIdentity } from '../entityPlan';
import { identityIdsToCheck, type TPlanChosen } from '../entityPlanIdentities';
import { useConfirmClient, type TConfirmEntityClient } from './useConfirmClient';
import { useLogPlanProblems } from './useLogPlanProblems';
import { useReportUnbackedChosen } from './useReportUnbackedChosen';
import { useSharedIdentityIds } from './useSharedIdentityIds';

export type { TConfirmEntityPlanState } from '../confirmEntityPlanState';
export type { TConfirmEntityClient } from './useConfirmClient';

/**
 * Always read fresh. `isFetchedAfterMount` (in `deriveConfirmEntityPlanState`) is what actually
 * keeps a previous mount's cached answer off the screen; these only make sure a fetch happens and
 * nothing lingers.
 */
const FRESH_READ = { refetchOnMount: 'always', staleTime: 0, cacheTime: 0 } as const;

/** What the plan needs beyond the client and the choice. */
export type TConfirmEntityPlanOptions = {
  /** What the customer chose on the choose screen: a wallet identity, or none for a new entity. */
  chosen?: TPlanChosen;
  /** The open entity request being worked on, when there are two (the request path). */
  entityRequestType?: TEntityRequestType;
  /** The organisation name the partner declared for a new entity, as given. */
  declaredOrganisationName?: string;
  /**
   * Closes the change link once a Draft of the open entity request's type exists (#616). Off by
   * default: the customer portal always turns it on (#689, no toggle).
   */
  closeChangeOnEntityDraft?: boolean;
};

/**
 * Everything the confirm screen shows for one tracking reference: the client (name and logo from
 * the workflow metadata) and the plan for `choice` (or, without one, for the client's confirmed
 * choice), built with the customer's permission requests for that client and the customer's
 * identities. The rules live in `deriveConfirmEntityPlanState`.
 *
 * The identities are read here rather than through `useAPIIdentities`, which neither exposes
 * `isFetchedAfterMount` nor lets a malformed response through as such (it defaults it to `[]`).
 *
 * Which of the customer's identities are already shared with the client is read for every identity
 * that might be shared: the Individual, and the wallet identity chosen. A chosen identity the
 * wallet cannot back is planned as a new entity, and reported by reason only.
 */
export function useConfirmEntityPlan(
  trackingReference: string | undefined,
  choice?: TEntityChoice,
  client?: TConfirmEntityClient,
  {
    chosen,
    entityRequestType,
    declaredOrganisationName,
    closeChangeOnEntityDraft,
  }: TConfirmEntityPlanOptions = {},
): TConfirmEntityPlanState {
  const {
    organisationId,
    organisationName,
    metadata,
    isLoading: isLoadingMetadata,
    hasError: metadataError,
  } = useConfirmClient(trackingReference, client);

  const requestsQuery = hooksApi.useGetV1permissionrequestme(undefined, FRESH_READ);
  const identitiesQuery = hooksApi.useGetV1customerme(
    { queries: { recursive: false } },
    FRESH_READ,
  );

  const identityList = (identitiesQuery.data as { customerList?: TPlanIdentity[] } | undefined)
    ?.customerList;
  const sharedIdentities = useSharedIdentityIds(
    organisationId,
    useMemo(() => identityIdsToCheck(identityList ?? [], chosen), [identityList, chosen]),
    isLoadingMetadata,
  );

  const previousLoaded = useRef<TConfirmEntityPlanLoaded | undefined>(undefined);

  const state = useMemo(
    () =>
      deriveConfirmEntityPlanState({
        metadata: {
          isLoading: isLoadingMetadata,
          hasError: metadataError,
          organisationId,
          organisationName,
          logoUrl: metadata?.logoUrl ?? undefined,
        },
        choice,
        chosen,
        entityRequestType,
        sharedIdentities,
        declaredOrganisationName,
        requests: {
          isFetchedAfterMount: requestsQuery.isFetchedAfterMount,
          hasError: !!requestsQuery.error,
          data: requestsQuery.data,
        },
        identities: {
          isFetchedAfterMount: identitiesQuery.isFetchedAfterMount,
          hasError: !!identitiesQuery.error,
          data: identitiesQuery.data,
        },
        closeChangeOnEntityDraft,
        previous: previousLoaded.current,
      }),
    [
      isLoadingMetadata,
      metadataError,
      organisationId,
      organisationName,
      metadata,
      choice,
      chosen,
      entityRequestType,
      sharedIdentities,
      declaredOrganisationName,
      requestsQuery.isFetchedAfterMount,
      requestsQuery.error,
      requestsQuery.data,
      identitiesQuery.isFetchedAfterMount,
      identitiesQuery.error,
      identitiesQuery.data,
      closeChangeOnEntityDraft,
    ],
  );

  useEffect(() => {
    if (state.status === 'loaded') {
      previousLoaded.current = state;
    }
  }, [state]);

  useReportUnbackedChosen(state, identityList, chosen);
  useLogPlanProblems(state, !!requestsQuery.error || !!identitiesQuery.error);

  return state;
}
